import { FC } from "react";
import styled from "@emotion/styled";
import { css, Global } from "@emotion/react";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";

/**
 * The circle floats over the desktop, not an app surface, so the mock pins
 * white marks on the brand fill in BOTH themes — harvest-moon's inverted
 * icon.onAccent (#262626) would paint them near-black. Mock value: Paper.
 */
export const FLOATING_AGENT_ON_BRAND_COLOR = "#FEFEFE";

// The hosting BrowserWindow is transparent; nothing outside the widget's own
// surfaces may paint a background.
const transparentWindowStyles = css`
  html,
  body,
  #root {
    background: transparent !important;
    overflow: hidden;
  }
`;

export const FloatingAgentWindowGlobalStyles: FC = () => (
  <Global styles={transparentWindowStyles} />
);

/**
 * Boot-time loading state for the floating-agent window. Replaces the main
 * app's full-screen "Almost done…" overlay, which would paint an opaque
 * backdrop over the transparent widget window. Renders as the widget circle
 * itself with the sweep ring spinning around it, so boot flows seamlessly
 * into the interactive circle.
 */
export const FloatingAgentLoadingOverlay: FC<{ isVisible: boolean }> = ({ isVisible }) => {
  if (!isVisible) return <FloatingAgentWindowGlobalStyles />;

  return (
    <>
      <FloatingAgentWindowGlobalStyles />
      <StyledLoadingViewport>
        <StyledLoadingCircleBox>
          <StyledLoadingRing viewBox="0 0 100 100" aria-hidden="true">
            <circle className="ring-track" cx="50" cy="50" r="46" />
            <circle className="ring-sweep" cx="50" cy="50" r="46" />
          </StyledLoadingRing>
          <StyledLoadingCircle>
            <MDSIcon
              kind={MDSIconKind.Logo}
              size={48}
              boxSize={48}
              overrideColor={FLOATING_AGENT_ON_BRAND_COLOR}
            />
          </StyledLoadingCircle>
        </StyledLoadingCircleBox>
      </StyledLoadingViewport>
    </>
  );
};

const StyledLoadingViewport = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

/* Mirrors the interactive circle's geometry (80px window, 64px circle,
   ring in the full window box) so the loading state morphs invisibly into
   the real widget once the app is up. */
const StyledLoadingCircleBox = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLoadingRing = styled.svg`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: visible;

  .ring-track {
    fill: none;
    stroke: ${({ theme }) => theme.colors.border.default};
    stroke-width: 2;
    opacity: 0.4;
  }

  /* Same seamless-loop geometry as the PTT sweep: dash + gap = 2π×46 ≈ 289,
     one keyframe period maps the pattern onto itself. */
  .ring-sweep {
    fill: none;
    stroke: ${({ theme }) => theme.colors.text.link.default};
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 70 219;
    transform-origin: center;
    transform: rotate(-90deg);
    opacity: 0.8;
    animation: floating-agent-loading-sweep 1.4s linear infinite;
  }

  @keyframes floating-agent-loading-sweep {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -289;
    }
  }
`;

const StyledLoadingCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.icon.brand};
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${FLOATING_AGENT_ON_BRAND_COLOR};
`;
